// Libraries
import { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
// Models
import { RootState } from '@redux/rootReducer';
import { IItemEmployee } from '@models/Employee';
import { IPlanEmployer } from '@models/Plans';
import { ICompanyInformation, IHomeInformation } from '@models/Company';
// Redux
import { useAppDispatch, useAppSelector } from '@hooks/redux';
import { setFunderId, setFullFunctionalityAvaiable } from '@redux/company/company.slice';
import { getCurrentHomePeriod } from '@redux/payslips/payslips.slice';
import { setGuideTour, setLoginUser } from '@redux/session/session.slice';
import { getPlansEmployee } from '@redux/plans/plans.slice';
import { getActiveEmployees } from '@redux/employee/employee.slice';
// Constants
import { Type } from '@pages/home/constants/InsidePlatform';
// Utils
import { getRoute } from '@utils/Paths';
// Components
import { Routes } from '@components/paths';
import { NOMINA_EN_LINEA_PRODUCT, TypeDocumentNames } from '@constants/Onboarding';
import { useOnboardingActions } from '@hooks/useOnboardingActions';
import { MAX_DAYS_REMEMBERANCE } from '../remembrance/constants/Remembrance';
import { TypesDocumentIds } from '@pages/employees/constants/Employees';
import { getDocumentTypeId } from '@utils/TypeDocuments';
import { getCompanyDueDiligenceStatus } from '@redux/onboarding/onboarding.slice';
import { DueDiligenceStatus, TDueDiligenceModalType } from '@constants/DueDiligence';

export const useInitializeData = (): {
    data: IHomeInformation | null;
    openRenewalBanner: boolean;
    openDueDiligenceBanner: boolean;
    openTermsAndConditionsModal: boolean;
    dueDiligenceModal: string | null;
    openContractsTooltip: boolean;
    employees: IItemEmployee[];
    activePlan: IPlanEmployer | null;
    plansEmployee: IPlanEmployer[] | [];
    isUsingFreeDemo: boolean;
    dueDiligenceLink: string;
    dueDiligenceStatus: string;
    showTyCErrorMessage: boolean;
    handleLiquidation: () => void;
    handleShowInformativeContractExpiration: () => void;
    handleStartTour: () => void;
    handleYearPeriodText: () => string;
    handleOpenDueDiligenceRedirecction: () => void;
    openDocumentPdf: (documentName: string, documentProduct: string) => void;
    handleShowTermsAndConditionsModal: (value: boolean) => void;
    approveNewTermsAndConditionsVersion: () => void;
    validateDueDiligenceStatus: () => Promise<void>;
} => {
    const {
        plans: { activePlan, plansEmployee },
        company: { id, home_information, information },
        employee: { activeEmployees },
        payslips: { currentYearPeriod },
        session: { loginUser, activeTour },
        onboarding: { urlEncrypt, dataProcessing, dueDiligence },
    } = useAppSelector((state: RootState) => state);

    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { getDocumentInformation, openDocumentPdf, approveDocuments } = useOnboardingActions();

    const [openContractsTooltip, setOpenContractsTooltip] = useState<boolean>(false);
    const [openTermsAndConditionsModal, setTermsAndConditionsPending] = useState<boolean>(false);
    const [showTyCErrorMessage, setShowTyCErrorMessage] = useState<boolean>(false);
    const [dueDiligenceModal, setDueDiligenceModal] = useState<TDueDiligenceModalType>(null);
    const hasFetchedDueDiligence = useRef(false);

    const isUsingFreeDemo = !!activePlan?.free_Plan;
    const prepaidPlanExist = useMemo(() => {
        return plansEmployee.some(plan => plan.pendingActivationPlan);
    }, [plansEmployee]);
    const needToRenewal = useMemo(() => {
        if (!activePlan) return true;
        const activePlanFinishDate = moment(activePlan.finish_date).startOf('day');
        const todayDate = moment().startOf('day');
        return activePlanFinishDate.diff(todayDate, 'day') <= MAX_DAYS_REMEMBERANCE;
    }, [activePlan]);
    const openRenewalBanner = useMemo(() => {
        if (plansEmployee.length === 0) return false;
        return needToRenewal && !prepaidPlanExist;
    }, [needToRenewal, prepaidPlanExist, plansEmployee]);
    const openDueDiligenceBanner = useMemo(() => {
        if (isUsingFreeDemo) return false;
        return dueDiligence === DueDiligenceStatus.NOTIFIED || dueDiligence === DueDiligenceStatus.FOR_NEW_CREATION;
    }, [dueDiligence, isUsingFreeDemo]);

    useEffect(() => {
        if (!activeTour && !information?.pila_user && loginUser?.lastLogin) {
            navigate(getRoute(Routes.COMPANY));
        }
        if (loginUser?.lastLogin) {
            dispatch(getCurrentHomePeriod(Type.PAYROLL, moment().year().toString()));
            dispatch(getActiveEmployees());
        }
    }, []);

    useEffect(() => {
        id && dispatch(getPlansEmployee());
    }, [id]);

    useEffect(() => {
        if (!information) return;
        if (loginUser?.lastLogin) {
            dispatch(setFunderId(information?.type_funder_id));
        }
        handleShowInitialTour(information);
    }, [information]);

    useEffect(() => {
        handleDisableFunctions();
    }, [dataProcessing, plansEmployee, activePlan, isUsingFreeDemo]);

    useEffect(() => {
        if (hasFetchedDueDiligence.current) return;
        if (!information?.name) return;
        if (!information?.document_type_id) return;
        if (!information?.document_number) return;

        hasFetchedDueDiligence.current = true;
        validateDueDiligenceStatus();
    }, [information?.name, information?.document_type_id, information?.document_number]);

    useEffect(() => {
        const shouldOpenRedirectModal = dueDiligence === DueDiligenceStatus.NO_CREATION && activePlan && !activePlan.free_Plan;
        if (shouldOpenRedirectModal) {
            setDueDiligenceModal('REDIRECT');
        }
    }, [dueDiligence, activePlan]);

    /**
     * redirect page
     */
    const handleLiquidation = (): void => {
        navigate(getRoute(Routes.PAYROLL_ADJUSTER));
    };

    /**
     * Action to show informative date component
     */
    const handleShowInformativeContractExpiration = (): void => {
        setOpenContractsTooltip(prev => !prev);
    };

    /**
     * Action to show terms and conditions modal
     */
    const handleShowTermsAndConditionsModal = (modalStage: boolean): void => {
        setTermsAndConditionsPending(modalStage);
    };

    /**
     * Action to set the full functionality validation
     */
    const handleDisableFunctions = (): void => {
        const termsAndConditionsPending = getDocumentInformation(TypeDocumentNames.TERMS_CONDITIONS, NOMINA_EN_LINEA_PRODUCT);
        const isNotOnlyFreePlan = plansEmployee.some(plan => !plan.free_Plan);
        if (termsAndConditionsPending && !isUsingFreeDemo && isNotOnlyFreePlan) {
            dispatch(setFullFunctionalityAvaiable(false));
            setTermsAndConditionsPending(true);
            return;
        }
        if (!activePlan) {
            dispatch(setFullFunctionalityAvaiable(false));
            return;
        }
        dispatch(setFullFunctionalityAvaiable(true));
    };

    /**
     * Action to approve the terms and conditions
     */
    const approveNewTermsAndConditionsVersion = async (): Promise<void> => {
        const companyUserId = information.users && information.users[0].user_id ? information.users[0].user_id : null;
        const documentNumber = information.document_number ? information.document_number : null;
        const documentTypeId = information.document_type_id ? information.document_type_id : null;
        if (companyUserId && documentNumber && documentTypeId) {
            const documenType = documentTypeId === TypesDocumentIds.NIT ? 'NIT' : 'CC';
            const approveDocumentesResponse = await approveDocuments(
                companyUserId,
                documenType,
                documentNumber,
                [TypeDocumentNames.TERMS_CONDITIONS],
                [NOMINA_EN_LINEA_PRODUCT],
                true
            );
            if (!approveDocumentesResponse) {
                setShowTyCErrorMessage(true);
                return;
            }
            setTermsAndConditionsPending(false);
        }
    };

    /**
     * Action to reinitialize guide tour
     */
    const handleStartTour = (): void => {
        dispatch(setGuideTour(false));
        dispatch(
            setLoginUser({
                userId: loginUser?.userId,
                lastLogin: null,
            })
        );
    };

    /**
     * Action to validate year period text
     * @returns {string} - Return if current year period is biweekly or monthly
     */
    const handleYearPeriodText = (): string => {
        const monthlyText = 'mes';
        const biweeklyText = 'quincenal';

        if (currentYearPeriod?.name) {
            const biweekly = 'Quincena';
            const regex = new RegExp(biweekly, 'gi');
            return regex.test(currentYearPeriod?.name) ? biweeklyText : monthlyText;
        }
        return monthlyText;
    };

    const handleShowInitialTour = (companyData: ICompanyInformation): void => {
        const isPilaUserNotExist = !companyData?.pila_user || !companyData?.pila_password;
        isPilaUserNotExist && handleStartTour();
    };

    /**
     * Action to dispatch the due diligence status and shows any error ocurred
     */
    const validateDueDiligenceStatus = async (): Promise<void> => {
        const companyName = information?.name;
        const companyDocumentType = information?.document_type_id ? getDocumentTypeId(information?.document_type_id) : null;
        const companyDocumentNumber = information?.document_number;
        if (!companyName || !companyDocumentType || !companyDocumentNumber) {
            setDueDiligenceModal('ERROR');
            return;
        }
        try {
            const response = await dispatch(
                getCompanyDueDiligenceStatus(companyName, companyDocumentType, companyDocumentNumber)
            );
            if (response.dueDiligenceStatus === DueDiligenceStatus.ERROR) setDueDiligenceModal('ERROR');
            if (response.dueDiligenceStatus === DueDiligenceStatus.APPROVED) setDueDiligenceModal(null);
        } catch {
            setDueDiligenceModal('ERROR');
        }
    };

    /**
     * Action to hanlde redirect Due diligence
     */
    const handleOpenDueDiligenceRedirecction = (): void => {
        setDueDiligenceModal('UPDATE');
        window.open(urlEncrypt, '_blank', 'noreferrer');
    };

    return {
        data: home_information,
        openRenewalBanner,
        openDueDiligenceBanner,
        openTermsAndConditionsModal,
        dueDiligenceModal,
        openContractsTooltip,
        employees: activeEmployees,
        activePlan,
        plansEmployee,
        isUsingFreeDemo,
        dueDiligenceLink: urlEncrypt,
        dueDiligenceStatus: dueDiligence,
        showTyCErrorMessage,
        handleLiquidation,
        handleShowInformativeContractExpiration,
        handleStartTour,
        handleYearPeriodText,
        handleOpenDueDiligenceRedirecction,
        openDocumentPdf,
        handleShowTermsAndConditionsModal,
        approveNewTermsAndConditionsVersion,
        validateDueDiligenceStatus,
    };
};
